import {
	annuallyFileReport,
	mdAnnualClaim,
	mdAnnualDistribution,
	mdAnnualSale,
	mdMonthlyClaim,
	mdMonthlyDistribution,
	mdMonthlySale,
	mdQuarterlyClaim,
	mdQuarterlySale,
	monthlyFileReport,
	quarterlyFileReport,
	reportTypes,
	sdAnnualAuditedFinance,
	sdAnnualAuditedInvestment,
	sdAnnualUnauditedFinance,
	sdQuarterlyFinance,
	sdQuarterlyInvestment,
	semesterlyFileReport,
} from './Constants';

const isSdGiUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('ews') && user?.ews?.includes('ewsCanViewStatementGeneralInsurance');
};

const isSdLiUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('ews') && user?.ews?.includes('ewsCanViewStatementLifeInsurance');
};

const isSdMiUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('ews') && user?.ews?.includes('ewsCanViewStatementMicroInsurance');
};

const isSdReUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('ews') && user?.ews?.includes('ewsCanViewStatementReinsurance');
};

const isMdGiUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('mdd') && user?.mdd?.includes('mddgmr') && user?.mdd?.includes('mddcae');
};

const isMdLiUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('mdd') && user?.mdd?.includes('mddlmr') && user?.mdd?.includes('mddcae');
};

const isMdMiUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('mdd') && user?.mdd?.includes('mddmmr') && user?.mdd?.includes('mddcae');
};

const isFileReportUser = (user) => {
	const roles = user?.role.split(',') || [];
	const reportTypes = user?.companyReportType?.split(',') || [];
	return roles?.includes('companySubmissionReport') && reportTypes?.includes('NEW_TYPE');
};

const isAdminUser = (user) => {
	const roles = user?.role.split(',') || [];
	return roles?.includes('admin') ? true : false;
};

const isPermittedUser = (user) => {
	const isAdmin = isAdminUser(user);
	const isPermittedMdUser = isMdGiUser(user) || isMdLiUser(user) || isMdMiUser(user);
	const isPermittedSdUser = isSdGiUser(user) || isSdLiUser(user) || isSdMiUser(user) || isSdReUser(user);
	const isPermittedFileUser = isFileReportUser(user);
	return isAdmin || isPermittedMdUser || isPermittedSdUser || isPermittedFileUser;
};

const getPermittedCompanyTypes = (user) => {
	const mdTypes = [];
	const sdTypes = [];
	const fileTypes = [];
	const result = {};

	if (isAdminUser(user)) {
		mdTypes.push('GENERAL_INSURANCE', 'LIFE_INSURANCE', 'MICRO_INSURANCE');
	} else {
		if (isMdGiUser(user)) {
			mdTypes.push('GENERAL_INSURANCE');
		}
		if (isMdLiUser(user)) {
			mdTypes.push('LIFE_INSURANCE');
		}
		if (isMdMiUser(user)) {
			mdTypes.push('MICRO_INSURANCE');
		}
	}

	if (isAdminUser(user)) {
		sdTypes.push('GENERAL_INSURANCE', 'LIFE_INSURANCE', 'MICRO_INSURANCE', 'REINSURANCE');
	} else {
		if (isSdGiUser(user)) {
			sdTypes.push('GENERAL_INSURANCE');
		}
		if (isSdLiUser(user)) {
			sdTypes.push('LIFE_INSURANCE');
		}
		if (isSdMiUser(user)) {
			sdTypes.push('MICRO_INSURANCE');
		}
		if (isSdReUser(user)) {
			sdTypes.push('REINSURANCE');
		}
	}

	if (isAdminUser(user) || isFileReportUser(user)) {
		fileTypes.push(
			'ASSOCIATION',
			'GENERAL_INSURANCE',
			'LIFE_INSURANCE',
			'MICRO_INSURANCE',
			'REINSURANCE',
			'INSURANCE_BROKER',
			'INSURANCE_AGENT',
			'INSURANCE_LOSS_ADJUSTER',
			'AUDIT_SERVICE',
			'INSURANCE_CLAIM_MANAGEMENT',
			'ACTUARIAL_FIRM',
		);
	}

	result['md'] = mdTypes;
	result['sd'] = sdTypes;
	result['file'] = fileTypes;

	return result;
};

const getPermittedReportTypes = (user) => {
	const mdTypes = [];
	const sdTypes = [];
	const fileTypes = [];
	const result = {};

	if (isAdminUser(user) || isMdGiUser(user) || isMdLiUser(user) || isMdMiUser(user)) {
		mdTypes.push(
			mdMonthlySale,
			mdMonthlyClaim,
			mdMonthlyDistribution,
			mdQuarterlySale,
			mdQuarterlyClaim,
			mdAnnualSale,
			mdAnnualClaim,
			mdAnnualDistribution,
		);
	}

	if (isAdminUser(user) || isSdGiUser(user) || isSdLiUser(user) || isSdMiUser(user) || isSdReUser(user)) {
		sdTypes.push(sdQuarterlyFinance, sdQuarterlyInvestment, sdAnnualAuditedFinance, sdAnnualAuditedInvestment, sdAnnualUnauditedFinance);
	}

	if (isAdminUser(user) || isFileReportUser(user)) {
		fileTypes.push(monthlyFileReport, quarterlyFileReport, semesterlyFileReport, annuallyFileReport);
	}

	result['md'] = mdTypes;
	result['sd'] = sdTypes;
	result['file'] = fileTypes;

	return result;
};

const isPermittedReportType = (user, type) => {
	const result = getPermittedReportTypes(user);
	const mdTypes = result['md'] ?? [];
	const sdTypes = result['sd'] ?? [];
	const fileTypes = result['file'] ?? [];
	return mdTypes?.includes(type) || sdTypes?.includes(type) || fileTypes?.includes(type);
};

const getReportName = (type, lang) => {
	const report = reportTypes?.find((r) => r.value.toLowerCase() === type.toLowerCase());
	return report ? (lang === 'kh' ? report.labelKh : report.labelEn) : type;
};

const getTypeName = (reportType, typeValue, t) => {
	const quarterMapping = {
		1: t('q1'),
		2: t('q2'),
		3: t('q3'),
		4: t('q4'),
	};
	const monthMapping = {
		0: t('January'),
		1: t('February'),
		2: t('March'),
		3: t('April'),
		4: t('May'),
		5: t('June'),
		6: t('July'),
		7: t('August'),
		8: t('September'),
		9: t('October'),
		10: t('November'),
		11: t('December'),
	};
	if (reportType === mdMonthlySale?.value || reportType === mdMonthlyClaim?.value || reportType === mdMonthlyDistribution?.value) {
		return monthMapping[typeValue] || typeValue;
	} else if (
		reportType === mdQuarterlySale?.value ||
		reportType === mdQuarterlyClaim?.value ||
		reportType === sdQuarterlyFinance?.value ||
		reportType === sdQuarterlyInvestment?.value
	) {
		return quarterMapping[typeValue] || typeValue;
	}
	return typeValue;
};

const getTypeValue = (type, typeValue, fileReport, lang, t) => {
	if (fileReport) {
		return lang === 'kh' ? fileReport?.titleKh : fileReport?.title;
	} else {
		if (typeValue !== undefined && typeValue !== null) {
			return getTypeName(type, typeValue, t);
		} else {
			return 'N/A';
		}
	}
};

export { isAdminUser, isPermittedUser, getPermittedCompanyTypes, getPermittedReportTypes, isPermittedReportType, getReportName, getTypeName, getTypeValue };
